import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useOnboarding } from './OnboardingContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AuthCallback — processes the Google OAuth return.
 * Emergent redirects to {origin}#session_id=XXXX. We exchange it once for a
 * session cookie, hydrate the user, then resume onboarding (or enter the site).
 */
export default function AuthCallback({ onDone }) {
    const { setAuthUser, setPhase, setAuthResumed } = useOnboarding();
    const processed = useRef(false);

    useEffect(() => {
        if (processed.current) return;
        processed.current = true;

        const hash = window.location.hash || '';
        const match = hash.match(/session_id=([^&]+)/);
        const sessionId = match ? decodeURIComponent(match[1]) : null;

        // Clean the fragment from the URL immediately
        window.history.replaceState(null, '', window.location.pathname + window.location.search);

        if (!sessionId) {
            setPhase('closed');
            onDone?.();
            return;
        }

        (async () => {
            try {
                // Remember-me preference chosen on the sign-in screen
                const remember = (() => {
                    try { return localStorage.getItem('sf.remember') !== '0'; } catch { return true; }
                })();
                const res = await axios.post(
                    `${API}/auth/session?remember=${remember ? 1 : 0}`,
                    {},
                    { headers: { 'X-Session-ID': sessionId }, withCredentials: true },
                );
                const srv = res.data;
                setAuthUser({ ...srv, remember });
                // ONLY drop into the folio if the new Metis / CBSE onboarding is complete.
                const complete =
                    !!srv.time_commitment &&
                    Array.isArray(srv.focus_areas) &&
                    srv.focus_areas.length > 0 &&
                    !!srv.primary_objective;
                if (complete) {
                    setPhase('inside');
                } else {
                    // Resume onboarding at question 1 (auth already done)
                    setAuthResumed(true);
                    setPhase('onboarding');
                }
            } catch {
                setPhase('closed');
            } finally {
                onDone?.();
            }
        })();
    }, [setAuthUser, setPhase, setAuthResumed, onDone]);

    return (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-[color:var(--moss-deep)]">
            <motion.div
                className="flex items-center gap-1.5"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
            >
                {[0, 1, 2].map((i) => (
                    <motion.div
                        key={i}
                        className="h-2 w-2 rounded-full bg-[color:var(--lavender)]"
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
                    />
                ))}
            </motion.div>
        </div>
    );
}
